import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { INDIA_STATES_TAMIL } from '@/data/states';
import { debounceTime, finalize, fromEvent, Subject, Subscription, takeUntil } from 'rxjs';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute } from '@angular/router';
import { INDIA_STATE_DISTRICTS } from '@/data/state-districts';
import Swal from 'sweetalert2';
import { EventBookingDetails } from '@/modals/event-details/eventBookingDetails';

@Component({
  selector: 'app-volunteer',
  templateUrl: './volunteer.component.html',
  styleUrl: './volunteer.component.scss'
})
export class VolunteerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('pageHeader', { static: true })
  pageHeader!: ElementRef<HTMLElement>;

  @ViewChild('bgWrapper', { static: true })
  bgWrapper!: ElementRef<HTMLElement>;

  statesList = INDIA_STATES_TAMIL;
  districtList: any[] = [];
  currentYear = new Date().getFullYear();
  resizeSub?: Subscription;

  eventId = 0;
  eventDetails: EventBookingDetails[];
  currentEventDetails : EventBookingDetails;

  destroy$: Subject<boolean> = new Subject<boolean>();

  private skipUpdateRows = false;

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private renderer: Renderer2,
    private route: ActivatedRoute
  ) { }

  // ---------------- FORM INITIALIZATION ----------------
  volunteerForm = this.fb.group({
    volunteerDetailsSeq: (0),
    age: ['', Validators.required],
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
    createdAt: [0],
    whatsappNumber: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]],
    state: ['', Validators.required],
    city: [''],
    district: ['', Validators.required],
    gender: ['', Validators.required],
    pledgeStatus: ['', Validators.requiredTrue],
    userName: [''],
    password: [''],
    status: ['InActive'],
    appRequired: ['No'],
    eventId: [0],
  });

  // ------------------- ngOnInit -------------------
  ngOnInit() {

    this.eventDetailsGetAll();

    // 4️⃣ Auto copy phone → whatsapp
    this.volunteerForm.get('phone')?.valueChanges.subscribe((value) => {
      if (value && value.length === 10) {
        this.volunteerForm.get('whatsappNumber')?.setValue(value, { emitEvent: false });
      }
    });

    // 6️⃣ State → District
    this.volunteerForm.get('state')?.valueChanges.subscribe((stateValue) => {
      this.loadDistricts(stateValue);
    });
  }

  // ---------------- HEADER MARGIN ADJUST ----------------
  ngAfterViewInit() {
    this.adjustContentMargin();
    this.resizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(120))
      .subscribe(() => this.adjustContentMargin());
  }

  adjustContentMargin() {
    const headerEl = this.pageHeader?.nativeElement;
    const wrapperEl = this.bgWrapper?.nativeElement;
    if (headerEl && wrapperEl) {
      const height = headerEl.offsetHeight;
      this.renderer.setStyle(wrapperEl, 'margin-top', `${height}px`);
    }
  }

  // ------------------- HELPERS -------------------
  loadDistricts(stateValue: string) {
    this.districtList = INDIA_STATE_DISTRICTS[stateValue] || [];
    this.volunteerForm.get('district')?.setValue('');
  }

  allowOnlyNumbers(event: any) {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  }

  // -------------------- SUBMIT --------------------
  submitForm() {

    if (this.volunteerForm.invalid) {
      this.volunteerForm.markAllAsTouched();
      return;
    }

    /** +91 attach */
    const phone = this.volunteerForm.get('whatsappNumber')?.value;
    if (phone && !phone.startsWith('91')) {
      this.volunteerForm.get('whatsappNumber')?.setValue('91' + phone, { emitEvent: false });
    }

    this.volunteerForm.get('city')?.setValue(this.volunteerForm.get('district').value);

    const finalFormData = this.volunteerForm.getRawValue();
    console.log("FINAL SUBMIT:", finalFormData);
    this.saveVolunteerDetail(finalFormData);
  }



  saveVolunteerDetail(data: any) {
    this.spinner.show();

    this.appService.saveVolunteerDetail(data)
        .pipe(finalize(() => this.spinner.hide()))
        .subscribe({

            next: async (res) => {

                // ----------------------------------
                // SUCCESS ALERT
                // ----------------------------------
                await Swal.fire({
                    icon: 'success',
                    title: 'தன்னார்வலர் பதிவு வெற்றிகரமாக முடிந்தது!',
                    html: 'Volunteer Registration Saved Successfully!',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#28a745',
                    allowOutsideClick: false
                });

                // ----------------------------------
                // 2WHATSAPP GROUP JOIN ALERT
                // ----------------------------------
                const groupLink = this.currentEventDetails?.volunteerWhatsappGroupLink;

                if (groupLink) {

                    const groupQrUrl =
                        'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' +
                        encodeURIComponent(groupLink);

                    await Swal.fire({
                        icon: 'info',
                        title: 'வாட்ஸ் ஆப் குழுவில் சேருங்கள்',
                        html: `
                          <p style="font-size:15px;text-align:center">
                            <b>Volunteer WhatsApp குழுவில் சேர:</b><br><br>
                            🔹 WhatsApp link கிளிக் செய்யவும் அல்லது<br>
                            🔹 QR Code scan செய்யவும்
                          </p>
                          <div style="text-align:center">
                            <img src="${groupQrUrl}" width="180" />
                          </div>
                        `,
                        confirmButtonText: 'WhatsApp குழுவில் சேர',
                        confirmButtonColor: '#25D366',
                        allowOutsideClick: false,
                        preConfirm: () => window.open(groupLink, '_blank')
                    });
                }

                // ----------------------------------
                // 3️⃣ RESET & RELOAD
                // ----------------------------------
                this.volunteerForm.reset();
                window.location.reload();
            },

            error: (err) => {
                const msg = err.error?.message || err.message;
                this.toastr.error(msg || 'Registration failed, please try again');
            }
        });
}

  eventDetailsGetAll() {
    this.spinner.show();
    this.appService.eventDetailsGetAll()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        const today = new Date().setHours(0, 0, 0, 0);
        const sorted = data
          .sort((a, b) => {
            const aIsFuture = a.startDate >= today;
            const bIsFuture = b.startDate >= today;
            if (aIsFuture && !bIsFuture) return -1;
            if (!aIsFuture && bIsFuture) return 1;
            return a.startDate - b.startDate;
          });
        this.eventDetails = sorted;
        console.log(`all events::`, this.eventDetails);
        this.spinner.hide();
      }, (error: any) => {
        this.spinner.hide()
        this.toastr.error(error?.error?.message || "Something Went Wrong, Please Try Again")
      });
  }

  onHeaderImageLoad(): void {
    // Small timeout ensures browser layout has stabilized (optional)
    setTimeout(() => {
      this.adjustContentMargin();
    }, 20);
  }


  selectValue(event: any) {
    const eventId = +event.target.value;
    this.eventId = Number(eventId);
    this.currentEventDetails = this.eventDetails.find(item => item.eventId === this.eventId)
    this.volunteerForm.get('eventId').setValue(this.eventId)
  }


  formattedDate(epoch: number): string {
    const date = new Date(epoch);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  ngOnDestroy() {
    this.resizeSub?.unsubscribe();
  }
}